import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { NavLink } from '@/components/NavLink';
import { useAuth } from 'hooks/useAuth';
import { useGlobal } from 'hooks/useGlobal';
import { getFlatMenuLinks } from '@/menu-links';
import { cn } from 'helpers/cn';
import { useWebsites } from '@/state/useWebsites';
import { useAttention } from '@/state/useAttention';
import { BetaChip } from './ConsoleSidebar';
import { useRegisterSidePanel } from './side-panel-registry';

type WorkspacePanelProps = { children: React.ReactNode };

/**
 * The one scroll container in the app.
 *
 * Everything above this used to rely on the window scrolling, because
 * `SidebarInset` was only `min-h-svh` and never actually overflowed. The console
 * shell is a fixed-height grid instead, so the page body has to own its own
 * overflow — which is also what finally makes `position: sticky` work inside a
 * screen.
 */
export const WorkspacePanel = ({ children }: WorkspacePanelProps) => {
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Route changes reset the page body, not the window.
  useEffect(() => {
    if (!location.hash) scrollRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <section className="bg-panel border-line flex min-h-0 flex-1 flex-col overflow-hidden rounded-panel border">
      <Toolbar />
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto p-3 md:p-4">
        {children}
      </div>
      <StatusStrip />
    </section>
  );
};

const Toolbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isBeta] = useGlobal<boolean>('PAGE_BETA', false);
  const { website } = useWebsites();
  const { user } = useAuth();

  const segments = location.pathname.split('/').filter(Boolean);

  /**
   * The paths that are actually routes, from the same tree that registers them.
   *
   * `menu-links` mixes groups with routes: `/experiments` is a heading with no page
   * of its own, only `/experiments/library` and friends are real. Anything that
   * guesses a parent by trimming a segment has to know that, and hardcoding the list
   * means it goes stale the first time a section is added.
   */
  const routes = getFlatMenuLinks({ user })
    .map((link) => link.url)
    .filter((url): url is string => !!url && !url.includes(':'));

  /**
   * Pop a history entry if there is one, otherwise go to the nearest real route.
   *
   * React Router records its position in `history.state.idx`, so an idx of 0 means
   * this is the first entry and `navigate(-1)` would leave the app entirely. That is
   * the case whenever a link is opened in a new tab or a deep URL is pasted in.
   *
   * The fallback replaces rather than pushes. A push put the parent at idx 1, so the
   * next press popped straight back to the page Back had just left.
   */
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) return navigate(-1);
    navigate(resolveParentRoute({ pathname: location.pathname, routes }), { replace: true });
  };

  /**
   * A breadcrumb is navigation, so every segment but the last is a link to the
   * path it stands for. It used to be plain text, which looks like a trail and
   * behaves like a label - the worst of both.
   *
   * The website is the root crumb and points home; it names the scope every screen
   * below it is filtered by. A segment that is not a route on its own gets no href
   * rather than a link to a 404 - checked against the route tree rather than a
   * hardcoded list of section names, which would go stale.
   */
  const crumbs = [
    { label: website?.friendlyName || website?.url, href: '/' },
    ...segments.map((segment, index) => {
      const path = `/${segments.slice(0, index + 1).join('/')}`;
      return { label: segment, href: routes.includes(path) ? path : undefined };
    }),
  ].filter((crumb) => !!crumb.label);

  return (
    <div className="border-line flex shrink-0 items-center gap-2 border-b px-3 py-2.5">
      {/* One back control, and it always does something.
          Forward is gone: there is no way to know whether a forward entry exists, so
          it was a button that did nothing most of the time. The browser has one.
          Back falls through to the parent route when there is no history to pop -
          arriving on a deep link directly used to leave it dead. */}
      <ToolbarChevron label="Back" onClick={goBack}>
        <ChevronLeft className="size-4" strokeWidth={1.5} />
      </ToolbarChevron>

      <nav
        aria-label="Breadcrumb"
        className="border-line bg-field flex min-w-[120px] flex-1 items-center gap-1.5 rounded-lg border px-2.5 py-1.5"
      >
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          const label = <span className="truncate font-mono text-micro">{crumb.label}</span>;

          return (
            <span key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1.5">
              {index > 0 && <span className="text-dim font-mono text-micro">/</span>}
              {isLast ? (
                <span aria-current="page" className="bg-sel-bg text-foreground truncate rounded px-1.5 py-0.5">
                  {label}
                </span>
              ) : crumb.href ? (
                <NavLink to={crumb.href} className="text-dim hover:text-foreground min-w-0 transition-colors">
                  {label}
                </NavLink>
              ) : (
                <span className="text-dim min-w-0">{label}</span>
              )}
            </span>
          );
        })}
        {isBeta && <BetaChip />}
      </nav>

      {/* Where a screen puts controls that govern the whole page. The toolbar is
          the one strip that never scrolls, so a control parked here stays
          reachable without a screen having to mirror it further down.

          Desktop only. These are mirrors of controls that still exist in the page
          itself, and four selects will not fit beside a breadcrumb on a phone. */}
      {/* min-w-0, not shrink-0: short of room, a screen's mirrored controls give way (each screen
          keeps its own must-stay controls at shrink-0, as the experiment header does). */}
      <div id={TOOLBAR_SLOT_ID} className="hidden min-w-0 items-center gap-2 empty:hidden md:flex" />
    </div>
  );
};

type ResolveParentRouteParams = { pathname: string; routes: string[] };

/**
 * The nearest real route above this one.
 *
 * Climbs the path first, then falls back to the section's own landing page - for
 * `/experiments/5898` that is `/experiments/library`, because `/experiments` is a
 * heading and navigating to it renders the 404.
 *
 * That fallback is only for a path that is not a route of its own. A page that IS
 * one - `/settings/general`, `/experiments/library` - is already the section's
 * landing page, so the section lookup returned the page the reader was standing on
 * and Back did nothing at all when it was reached by deep link. Above a landing page
 * is the dashboard, which is above everything.
 *
 * Only the landing page itself goes home. Its siblings - `/surveys/new`,
 * `/websites/google-analytics` - still go to the landing page, as they did before.
 */
export const resolveParentRoute = ({ pathname, routes }: ResolveParentRouteParams) => {
  const segments = pathname.split('/').filter(Boolean);

  for (let depth = segments.length - 1; depth > 0; depth -= 1) {
    const candidate = `/${segments.slice(0, depth).join('/')}`;
    if (routes.includes(candidate)) return candidate;
  }

  const section = segments[0];
  const landing = routes.find((url) => url.startsWith(`/${section}/`));
  if (!landing || landing === pathname) return '/';
  return landing;
};

/** Portal target for `WorkspaceToolbarControls`. */
export const TOOLBAR_SLOT_ID = 'workspace-toolbar-slot';

type WorkspaceToolbarControlsProps = { children: React.ReactNode };

/**
 * Renders its children into the workspace toolbar from anywhere in the page.
 *
 * A screen that owns page-wide controls should not have to hand them up through
 * the layout, and the alternative - mirroring them in a bar that pins over the
 * content - covers the numbers the controls govern.
 *
 * The slot is resolved in an effect rather than during render: on first paint
 * the toolbar has not been committed yet, so `getElementById` would return null
 * and the controls would never appear.
 */
export const WorkspaceToolbarControls = ({ children }: WorkspaceToolbarControlsProps) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setSlot(document.getElementById(TOOLBAR_SLOT_ID));
  }, []);

  if (!slot) return null;
  return createPortal(children, slot);
};

/** Portal target for `WorkspaceSidePanel`, beside the workspace panel (DashboardLayout). */
export const SIDE_PANEL_SLOT_ID = 'workspace-side-panel-slot';

type WorkspaceSidePanelProps = { children: React.ReactNode };

/**
 * Renders its children as a column docked beside the workspace panel, from anywhere in the page:
 * the results AI chat (P-PER-44), which has to sit next to the numbers it explains rather than over
 * them. The slot is empty, and takes no room, until something is rendered into it. Below the xl
 * breakpoint there is no room for a column, and the slot covers the workspace's right edge instead.
 * Resolved in an effect, like the toolbar slot, because the layout commits after the page.
 *
 * While mounted it counts itself in (`side-panel-registry`), and the layout folds the navigation to
 * its rail for as long as any side panel is: the chat needs the width more than the labels do.
 */
export const WorkspaceSidePanel = ({ children }: WorkspaceSidePanelProps) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useRegisterSidePanel();

  useEffect(() => {
    setSlot(document.getElementById(SIDE_PANEL_SLOT_ID));
  }, []);

  if (!slot) return null;
  return createPortal(children, slot);
};

type ToolbarChevronProps = { label: string; onClick: () => void; children: React.ReactNode };

const ToolbarChevron = ({ label, onClick, children }: ToolbarChevronProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="text-dim hover:text-foreground hover:bg-hover flex size-7 items-center justify-center rounded-lg transition-colors duration-150"
  >
    {children}
  </button>
);

/**
 * Page title on the left, a live count of what needs attention on the right.
 * Both are read from state the app already keeps, so the strip cannot drift from
 * the dashboard.
 */
const StatusStrip = () => {
  const [pageTitle] = useGlobal<string>('PAGE_TITLE', '');
  const { website } = useWebsites();
  const { items } = useAttention(website?.id);

  const broken = items?.filter((item) => item.severity === 'broken').length ?? 0;
  const decide = items?.filter((item) => item.severity === 'decide').length ?? 0;

  return (
    <div className="border-line flex shrink-0 items-center justify-between gap-3 border-t px-3 py-2">
      <span className="micro-label truncate">{pageTitle}</span>
      <span className="micro-label shrink-0">
        {broken} {broken === 1 ? 'problem' : 'problems'} · {decide} to decide
      </span>
    </div>
  );
};
